import * as React from 'react';
import { useSelector } from 'react-redux';

import { DistrictInfrastructureCell } from '../../components/NewbuildingsComparePage/parts/Cell/cells/DistrictInfrastructureCell';
import { DistrictInfrastructureMapModal } from '../../components/DistrictInfrastructureMapModal';
import { IDistrictInfrastructureCell, INewbuildingSummaryCellValue } from '../../types/newbuildingsCompare';
import { YANDEX_MAP_API_KEY_CONFIG_KEY } from '../../constants/map';
import { selectDeviceType } from '../../selectors/page/selectDeviceType';
import { selectIsInfrastructureMapEnabled } from '../../selectors/page/selectIsInfrastructureMapEnabled';
import { useApplicationContext } from '../../utils/applicationContext';
import { useDistrictInfrastructure } from './useDistrictInfrastructure';
import {
  ITrackOpenDistrictMap,
  trackFutureYearChange,
  trackInfrastructureTypeClick,
  trackMapServiceClick,
  trackOpenDistrictMap,
  trackTaRatingOpen,
  trackTaSourceClick,
  trackTaTabChange,
} from './tracking';

export interface IDistrictInfrastructureMapContainerProps {
  cell: IDistrictInfrastructureCell;
  newbuilding?: INewbuildingSummaryCellValue;
}

/** Ячейка «Район» с кнопкой «На карте»: по клику открывает карту района с инфраструктурой. */
export function DistrictInfrastructureMapContainer({
  cell,
  newbuilding,
}: IDistrictInfrastructureMapContainerProps): React.JSX.Element {
  const { config } = useApplicationContext();
  const [isOpened, setOpenedState] = React.useState(false);
  const deviceType = useSelector(selectDeviceType);
  const platform = deviceType === 'desktop' ? 'desktop' : 'mobile';
  // Флаг резолвится на сервере и приезжает в initialState — иначе кнопка мигала бы на гидрации.
  const isEnabled = useSelector(selectIsInfrastructureMapEnabled);

  const apiKey = config.get<string>(YANDEX_MAP_API_KEY_CONFIG_KEY);
  const coordinates = cell.data?.coordinates;
  const canShowMap = Boolean(isEnabled && apiKey && coordinates);

  const { pois, polygon, future, transportAccessibility, taRating, load } = useDistrictInfrastructure({
    coordinates,
    newbuildingId: newbuilding?.id,
  });
  const trackingParams = React.useMemo<ITrackOpenDistrictMap>(
    () => ({ newbuildingId: newbuilding?.id, platform }),
    [newbuilding?.id, platform],
  );

  const handleOpen = React.useCallback(() => {
    setOpenedState(true);
    load();
    trackOpenDistrictMap({ newbuildingId: newbuilding?.id, platform });
  }, [load, newbuilding?.id, platform]);

  const handleClose = React.useCallback(() => {
    setOpenedState(false);
  }, []);

  return (
    <>
      <DistrictInfrastructureCell cell={cell} onMapClick={canShowMap ? handleOpen : undefined} />
      {isOpened && canShowMap && apiKey && coordinates && (
        <DistrictInfrastructureMapModal
          apiKey={apiKey}
          center={coordinates}
          future={future}
          newbuilding={newbuilding}
          platform={platform}
          pois={pois}
          polygon={polygon}
          taRating={taRating}
          title={cell.data?.title ?? ''}
          transportAccessibility={transportAccessibility}
          onClose={handleClose}
          onFutureYearChange={year => trackFutureYearChange({ ...trackingParams, year })}
          onModeChange={mode => mode !== 'default' && trackMapServiceClick({ ...trackingParams, mode })}
          onTypeClick={type => trackInfrastructureTypeClick({ ...trackingParams, type })}
          onTaRatingOpen={() => trackTaRatingOpen(trackingParams)}
          onTaSourceClick={() => trackTaSourceClick(trackingParams)}
          onTaTabChange={tab => trackTaTabChange({ ...trackingParams, tab })}
        />
      )}
    </>
  );
}
